import { useEffect, useMemo, useRef, useState } from 'react';
import CustomStepper from './CustomStepper'
import { enqueueSnackbar } from 'notistack';
import { JService } from '../api-service/ApiService';
import { getOrgURLParam } from '../../utility-functions';
import AnimationPage from '../shared/animation-page/AnimationPage';
import KycEmbed, { MAX_RETRIES } from "../kyc";
import { useLocation, useNavigate } from 'react-router-dom';
import "./upgradeStepper.css";
import SelectPlan from './SelectPlan';
import ConfigurePlan from './ConfigurePlan';
import PurchaseSummaryFinal from './PurchaseSummaryFinal';
import BillingAddress from './BillingAddress';
import { useSelector } from 'react-redux';

const STEPS = {
    CHOOSE_PLAN: 1,
    CONFIGURE: 2,
    BILLING_ADDRESS: 3,
    VERIFICATION: 4,
    LOADING: 5,
    BILLING: 6
}

const Upgrade = () => {
    const location = useLocation();
    const reduxSubscription = useSelector(
        (state) => state?.account?.value?.subscription
    );

    const navigate = useNavigate();

    const { isKycCompleted = false, isAgreementSigned = false } = location?.state || {};

    const [billingCycle, setBillingCycle] = useState("Monthly");
    const [userVerified, setUserVerified] = useState({
        isKycCompleted,
        isAgreementSigned
    });
    const [currentStep, setCurrentStep] = useState(STEPS.CHOOSE_PLAN);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [showLoader, setShowLoader] = useState(false);
    const [plansData, setPlansData] = useState([]);
    const [finalPurchaseData, setFinalPurchaseData] = useState({});
    const [selectedPlanType, setSelectedPlanType] = useState(null);
    const [agreementProcessCompleted, setAgreementProcessCompleted] = useState(false);

    const [isKyc, setIsKyc] = useState({
        isLoading: false,
        isOpen: false,
    });
    const [kycDetails, setKycDetails] = useState({
        session_id: null,
        secureme_link: null,
    });
    const [isAgreementStarted, setIsAgreementStarted] = useState(false);
    const [agreementDetails, setAgreementDetails] = useState({
        docket_id: null,
        signer_id: null,
        document_id: null
    });

    const retryCountRef = useRef(0);
    const isKycProcessCompleted = useMemo(() => userVerified?.isKycCompleted && userVerified?.isAgreementSigned, [userVerified]);

    useEffect(() => {
        if(reduxSubscription?.status === "ACTIVE") navigate("/activity-logs/calls/", {replace: true});
        const fetchPlans = () => {
            setShowLoader(true);
            let orgParam = getOrgURLParam();
            JService.get(`/api/v1/subscriptions/plans/${orgParam}`)
                .then((res) => {
                    if (!res?.success)
                        throw new Error(res?.message || "Could not retrieve plans");
                    setPlansData(res?.data);
                    setShowLoader(false);
                })
                .catch((err) => {
                    enqueueSnackbar(err?.message || "Could not retrieve plans", {
                        variant: "error",
                    });
                })
                .finally(() => {
                    setShowLoader(false);
                });
        };

        fetchPlans();
    }, []);

    const handleKyc = async () => {
        try {
            setIsKyc({
                isLoading: true,
                isOpen: true,
            })
            const response = await JService.post(
                "/api/v1/integrations/kyc/initiate",
            );
            setIsKyc({
                isLoading: false,
                isOpen: true
            })
            setKycDetails({
                session_id: response.session_id,
                secureme_link: response.secureme_link
            })
        } catch (error) {
            enqueueSnackbar(error?.message || "Failed to complete kyc. Please contact support@frejun.com", { variant: "error" });
            setIsKyc({
                isLoading: false,
                isOpen: false
            });
            setCurrentStep(STEPS.BILLING_ADDRESS);
        }
    };

    const handleAgreement = async () => {
        try {
            setIsAgreementStarted(true);
            const response = await JService.post("/api/v1/integrations/agreement/initiate");

            if (typeof window.esignWidgetCall !== "function") {
                throw new Error("Couldn't open the signing window. Disable ad blockers/VPN and refresh the page. Contact support@frejun.com if the issue persists.");
            }

            console.log("malento app id: ", process.env.REACT_APP_MELENTO_APP_ID);

            const options = {
                id: process.env.REACT_APP_MELENTO_APP_ID,
                docket_id: response.docket_id,
                signer_id: response.signer_id,
                document_id: response.document_id,
                widget_width: "90%",
                widget_height: "90%",

                getWidgetToken: async function (result) {
                    const response = await JService.post("/api/v1/integrations/agreement/widget-token", {
                        widget_token: result
                    });
                    setAgreementDetails({
                        docket_id: this.docket_id,
                        signer_id: this.signer_id,
                        document_id: this.document_id,
                    });
                },
                signatureStatus: function (result) {
                    console.log("in signature Status: ", result);
                    if (result.status === "rejected") {
                        enqueueSnackbar("Please sign the document.", { variant: "error" });
                        failedAgreement();
                    }
                    else if (result.status === "failed") {
                        enqueueSnackbar("Please try again.", { variant: "error" });
                        failedAgreement();
                    } else {
                        setAgreementProcessCompleted(true);
                    }
                },
                errorLog: function (err) {
                    console.log("errorLog-agreement: ", err);
                    enqueueSnackbar(err?.message || "Failed to initiate agreement. Please contact support@frejun.com", { variant: "error" });
                    failedAgreement();
                },
            };

            window.esignWidgetCall(options);
        } catch (error) {
            console.log("Error agreement: ", error);
            enqueueSnackbar(error?.message || "Failed to initiate agreement. Please contact support@frejun.com", { variant: "error" });
            failedAgreement();
        }
    };

    const failedAgreement = () => {
        console.log("Agreement process failed or rejected by user.");
        setAgreementDetails({
            docket_id: null,
            signer_id: null,
            document_id: null,
        });
        setIsAgreementStarted(false);
        setCurrentStep(STEPS.BILLING_ADDRESS);
    };

    const getResult = async () => {
        try {
            retryCountRef.current += 1;
            if (retryCountRef.current > MAX_RETRIES) {
                return;
            }
            const response = await JService.get(
                `/api/v1/integrations/agreement/document/status?document_id=${agreementDetails.document_id}`,
            );

            if (response.signing_status === "COMPLETED") {
                setCurrentStep(STEPS.BILLING);
                enqueueSnackbar("Agreement process is completed", { variant: "success" });
                handleOrganization();
            } else if (response.signing_status === "FAILED") {
                setCurrentStep(STEPS.BILLING_ADDRESS);
                enqueueSnackbar("Agreement process failed. Please contact support@frejun.com", { variant: "error" });
            } else {
                getResult();
            }

        } catch (err) {
            enqueueSnackbar(err?.message, { variant: "error" });
        }
    };

    useEffect(() => {
        if (!agreementDetails.document_id) return;
        if(agreementProcessCompleted) {
            getResult();
            setAgreementProcessCompleted(false);
        }
    }, [agreementDetails.document_id, agreementProcessCompleted]);

    const handleNextStep = (type) => {
        const displayName = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        setSelectedType(displayName);

        for (let i = 0; i < plansData.length; i++) {
            if (
                plansData[i]?.name?.split("-")[0]?.toUpperCase() === type &&
                plansData[i].billing_cycle === billingCycle?.toUpperCase()
            ) {
                setSelectedPlan(plansData[i]?.name);
                break;
            }
        }

        setCurrentStep(STEPS.CONFIGURE);
    };

    const handleConfigureContinue = (final_purchase_data) => {
        setFinalPurchaseData(final_purchase_data);
        setCurrentStep(STEPS.BILLING_ADDRESS);
    };

    const handleAddressContinue = () => {
        if (!userVerified.isKycCompleted) {
            setIsKyc({ isLoading: true, isOpen: true });
            setCurrentStep(STEPS.VERIFICATION);
            handleKyc();
        } else if (!userVerified.isAgreementSigned) {
            setCurrentStep(STEPS.LOADING);
            handleAgreement();
        } else {
            setCurrentStep(STEPS.BILLING);
        }
    };

    const handleOrganization = async () => {
        try {
            const res = await JService.get("/api/v1/auth/retrieve-organization/");
            if (!res?.success) {
                throw new Error(res?.message || "Something went wrong. Please try again!");
            }
            setUserVerified({
                isKycCompleted: res.data.is_kyc_completed,
                isAgreementSigned: res.data.is_agreement_signed
            })
        } catch (err) {
            enqueueSnackbar(err?.message ?? "Error occurred", { variant: "error" });
        }
    };

    const onKycPassed = () => {
        setCurrentStep(STEPS.LOADING);
        enqueueSnackbar("Verification is completed.", { variant: "success" });
        handleAgreement();
        handleOrganization();
    }
    useEffect(() => {
        handleOrganization();
    }, []);

    const getStepperStep = (step) => {
        if (step <= 3) return step;
        if (step <= 5) {
            if(isKycProcessCompleted) return 3;
            return 4;
        }
        if(isKycProcessCompleted) return 4;
        return 5;
    };

    return (
        <div style={{ marginTop: "1rem" }}>

            <div style={{ width: "100%", maxWidth: (currentStep === STEPS.CONFIGURE || currentStep === STEPS.BILLING_ADDRESS) ? "900px" : "800px", margin: 'auto', transition: "max-width 0.3s" }}>
                <CustomStepper currentStep={getStepperStep(currentStep)} isKycProcessCompleted={isKycProcessCompleted} style={{ width: isKycProcessCompleted ? "660px" : "800px", margin: 'auto' }} />

                {currentStep === STEPS.CHOOSE_PLAN && <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        marginTop: "1rem"
                    }}
                >
                    {showLoader ? <AnimationPage style={{ transform: "none" }} /> : 
                    <SelectPlan selectedPlanType={selectedPlanType} setSelectedPlanType={setSelectedPlanType} billingCycle={billingCycle} onClick={handleNextStep} plansData={plansData} setBillingCycle={setBillingCycle} isButtonLoading={isKyc.isLoading || isAgreementStarted} />}
                </div>} 

                {currentStep === STEPS.CONFIGURE && 
                    <ConfigurePlan
                        planName={selectedType}
                        billingCycle={billingCycle}
                        plansData={plansData}
                        onBack={() => setCurrentStep(STEPS.CHOOSE_PLAN)}
                        onContinue={handleConfigureContinue}
                        finalPurchaseData={finalPurchaseData}
                    />
                }

                {currentStep === STEPS.BILLING_ADDRESS && (
                    <BillingAddress onContinue={handleAddressContinue} onBack={() => setCurrentStep(STEPS.CONFIGURE)} isKycProcessCompleted={isKycProcessCompleted} {...finalPurchaseData} />
                )}

                {currentStep === STEPS.VERIFICATION && <div>
                    {isKyc.isLoading ?
                        <AnimationPage style={{ transform: "none" }} />
                        :
                        <div className="modal-overlay">
                            <div
                                className="modal-content"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <KycEmbed
                                    securemeLink={kycDetails.secureme_link}
                                    sessionId={kycDetails.session_id}
                                    onPassed={onKycPassed}
                                    onFailed={(msg) => {
                                        enqueueSnackbar(msg, { variant: "error" });
                                        setCurrentStep(STEPS.BILLING_ADDRESS);
                                    }}
                                    onRetry={() => {
                                        enqueueSnackbar("KYC process failed. Please retry.", { variant: "error" });
                                        handleKyc();
                                    }}
                                />
                            </div>
                        </div>}
                </div>}

                {currentStep === STEPS.LOADING && <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <AnimationPage style={{ transform: "none" }} />
                </div>}

                {currentStep === STEPS.BILLING &&
                    <PurchaseSummaryFinal {...finalPurchaseData} />
                }
            </div>
        </div>
    )
}

export default Upgrade;